import { BehaviorSubject } from "rxjs";

export interface Timeslot {
    uuid: string,
    start: string,
    end: string,
    palvelu: string,
    available?: boolean,
    hinta?: number
}

export interface TimeSpan {
    from: String,
    to: String
}

export interface TimeslotRange {
    from: string,
    to: string
}

interface Palvelu {
    uuid: string,
    erityisosaaminen?: string[]
}

export interface Expert {
    kieli: string[],
    palvelu: Palvelu[],
    yritys: { työtehtävä: string }[],
    toimipaikka: { uuid: string; role: boolean }[],
    Nimi: string,
    Kuvaus: string,
    Koulutus: string,
    erityisosaaminen?: string[]
}

export enum ApiTranslationKeys {
    suomeksi = "suomeksi",
    ruotsiksi = "ruotsiksi"
}

export interface TranslationObj {
    [ApiTranslationKeys.suomeksi]?: string,
    [ApiTranslationKeys.ruotsiksi]?: string
}

export interface Building {
    Nimi: string,
    Markkinointinimi: string,
    Sijaintikiinteisto: string,
    katuosoite?: string,
    kadunnimi?: TranslationObj,
    Postinumero?: string,
    location?: LocationCoords,
    dist?: number
}

export interface Premise {
    toimitila: string,
    palvelu: { uuid: string }[],
    palvelualue: string[],
    Nimi: {
        liiketoiminta: string,
        yritys: string,
        toimipaikka: string,
        toimitila: string
    },
    Verkkosivu: string,
    Ajo_ohjeet: string,
    Pysäköintiohjeet: string,
    Esteettömyystiedot: string,
    Ajanvaraus_Kuvaus: string,
    Ajanvaraus_Puhelin: string,
    combineToOffice: string,
    alkamispäivä?: string,
    päättymispäivä?: string
}

export interface AppointmentCancellationResponse {
    op: Operations.appointment, // Redundant field. ToDo: check if the frontend code uses it, clean if it does and then remove this.
    uuid: string,
    success: boolean
}

export interface AppointmentReservationSuccessResponse {
    op: Operations.appointment,  // Redundant
    uuid: string,
    success: true,
    data: {
        "cancellation pin": string
    }
}

export interface AppointmentReservationErrorResponse {
    op: Operations.appointment,  // Redundant
    uuid: string,
    success: false,
    data: {
        error: string
    }
}

export type ReservationResponses = RequestReservationResponseSuccess |
                                   RequestReservationResponseFailure;

export interface OpenAppointmentRangeResponse {
    op: Operations.appointment,   // This field is now redundant and should be removed eventually.
    range: {
        from: string,
        to: string
    },
    filter: string[],             // This field might be redundant in this context.
    data: string[]
}

export interface Appointment {
    uuid: string,
    palvelu: string,
    toimipaikka: string,
    työntekijä: string,
    start: string,
    end: string,
    hinta: number
}

export interface DayAppointmentListResponse {
    date: string,
    filter: string[],
    offset: number,
    limit: number,
    op: Operations.appointment,     // Probably a redundant field.
    total: number,
    data: Appointment[]
}

export interface TimeslotResponse extends Timeslot {
    työntekijä: string,
    tyontekija: string,
    toimipaikka: string
}

export interface PopulatedTimeslot extends Timeslot {
    dist?: number | null,
    työntekijä: EntityResponse<Expert>,
    toimipaikka: PremiseLocation | Premise,
    toimitila: PremiseLocation["toimitila"] | Building,
    palvelunNimi: string,
    toimipaikkaUuid: string,
    toimitilaUuid: string
}

export type LanguageSelection = "finnish" | "english" | "swedish";

export interface AuthCallbackState {
    reservationUuid: string,
    bookForOther: boolean,
    campaignUuid: string,
    language: LanguageSelection
}

export interface CampaignService {
    uuid: string,
    name: string,
    Kuvaus?: string,
    popular?: boolean,
    children?: CampaignService[],
    alkamispäivä?: string,
    päättymispäivä?: string,
    maksutapa: string
}

export interface LocationCoords {
    lat: number,
    lon: number
}

export interface PremiseLocation {
    uuid: string,
    Nimi: Premise["Nimi"],
    alkamispäivä?: string,
    päättymispäivä?: string,
    toimitila: { uuid: string } & Partial<Building>,
    dist?: number,
    Kunta?: string
}

export interface Locations {
    toimipaikka: PremiseLocation[]
}

export interface BasicExpert {
    uuid: string,
    Nimi: string
}

export interface Experts {
    työntekijä: BasicExpert[]
}

export interface Language {
    kieli: string
}

export interface BasicResponse {
    op: Operations
}

export interface EntityResponse<T> extends BasicResponse {
    uuid: string,
    ctx?: string,
    model: string,
    data: T,
    from?: string,
    limit?: number,
    total?: number
}

export interface DataResponse<T> extends BasicResponse {
    filter: string[],
    model: string,
    data: T,
    date?: string,
    from?: number,
    limit?: number,
    total?: number,
    range?: TimeslotRange
}

export interface RequestReservationResponse extends BasicResponse {
    uuid: string,
    success: boolean,
    timeout: boolean
}

export interface MapBoxTokenResponse {
    op: Operations.mapbox,
    ctx: Contexes.temporaryToken,
    success: boolean,
    data: {
        expires: string,
        token: string
    }
}

export interface RequestReservationResponseFailure extends RequestReservationResponse {
    success: false,
    data: {
        appointment?: "Not available",
        emailAddress?: "Invalid value",
        phoneNumber?: "Invalid value",
        streetAddress?: "Invalid value",
        error?: string
    }
}

export interface RequestReservationResponseSuccess extends RequestReservationResponse {
    success: true,
    data: {
        "cancellation_token": string
    }
}

export interface RequestReservationResponseTimeout extends RequestReservationResponse {
    success: false,
    timeout: true,
    data: {}
}

export interface CancelReservationResponse {
    op: Operations.appointment,
    uuid: string,
    success: boolean
}

export interface CancelReservationSuccessResponse extends CancelReservationResponse {
    success: true
}

export interface CancelReservationFailureResponse extends CancelReservationResponse {
    success: false,
    data: {
        error: string
    }
}

export interface StreetNumber {
    uuid: string,
    name: string,
    lon: number,
    lat: number
}

export interface Address {
    // nearMe?: boolean,
    address?: string,
    streetNumber: StreetNumber[],
    postalCode: string,
    locality?: string
}

export type CallbackFunc<T> = (resp: T) => void;

export enum Operations {
    auth = "auth",
    data = "data",
    search = "search",
    appointment = "appointment",
    suomiOsoitteet = "Suomi osoitteet",
    mapbox = "mapbox"
}

export enum Contexes {
    topaasi = "topaasi",
    toimipaikka = "toimipaikka",
    työntekijä = "työntekijä",
    toimitila = "toimitila",
    palvelu = "palvelu",
    kieli = "kieli",
    erityisosaaminen = "erityisosaaminen",
    temporaryToken = "temporary token"
}

export enum Models {
    palvelu = "palvelu",
    toimipaikka = "toimipaikka",
    työntekijä = "työntekijä",
    toimitila = "toimitila",
    kieli = "kieli",
    erityisosaaminen = "erityisosaaminen"
}

export interface DataStore<T> {
    subject: BehaviorSubject<EntityResponse<T>[]>,
    updateState: (
        subject: BehaviorSubject<EntityResponse<T>[]>,
        expert: EntityResponse<T>
    ) => Promise<void>,
    getValueByUuid: (uuid: string) => Promise<EntityResponse<T> | undefined>
}

export interface TimeslotStoreItems {
    from: number,
    limit: number,
    total: number,
    range: TimeslotRange | null,
    date: string | null,
    filter: string[],
    data: TimeslotResponse[],

    selectedExpertsUuids: string[],
    selectedPremisesUuids: string[],
    selectedServicesUuids: string[]
}

export interface TimeslotStore {
    subject: BehaviorSubject<TimeslotStoreItems>,
    updateState: (
        subject: BehaviorSubject<TimeslotStoreItems>,
        timeslots: DataResponse<TimeslotResponse[]>,
        selectedExpertsUuids: string[],
        selectedPremisesUuids: string[],
        selectedServicesUuids: string[],
        date: Date | null
    ) => Promise<void>,
    unsubscribe: () => void,
    getValueByUuid: (uuid: string) => Promise<TimeslotResponse | undefined>,
    updateTimeslotValues: (store: TimeslotStore, available: boolean, uuid: string) => void,
    addTimeslot: (store: TimeslotStore, available: boolean, item: TimeslotResponse) => void,
    removeTimeslot: (store: TimeslotStore, uuid: string) => void
}

export interface Languages {
    kieli: string[]
}

export interface Speciality {
    erityisosaaminen: string
}

export interface Specialities {
    erityisosaaminen: string[]
}

export interface Services {
    palvelu: { uuid: string }[]
}

export interface UserLocation {
    address: string,
    coordinates: LocationCoords
}

export interface LocationContainer {
    uuids: string[],
    lon: number,
    lat: number,
    container: HTMLDivElement,
    building?: EntityResponse<Building> | null,
    premise?: EntityResponse<Premise> | null
}

export type BuildEnv = "production" | "staging" | "development" | "test"

export interface Campaign {
    id: string,
    name: string,
    customerTypes: string[],
    mainCategoryUuid: string,
    subServices: SubCampaign[],
    path?: {
        finnish: string,
        swedish: string,
        english: string
    }
}

export interface SubCampaign {
    id: string,
    name: string,
    customerType: string
}

export interface SelectedFilters {
    selectedServices: CampaignService[],
    selectedPremises: EntityResponse<Premise>[],
    selectedExperts: EntityResponse<Expert>[],
    selectedSpecialities: EntityResponse<Speciality>[],
    selectedLanguages: EntityResponse<Language>[],
    customerType: string,
    mapLocationUuids: PremiseLocation[] | null,
    locationUuids: PremiseLocation[] | null,
    position: GeolocationPosition | null,
    userLocation: UserLocation | null
}
export type InputName = "customerType" | "searchService" |  "searchPremise";

// UI can have some service specific configurations that can be changed from services.json file.
// This interface describes different configuration options
export interface UISettings {
    // If the extra filters: expert's special skills, and the language shown by the default for the user or not.
    expandDesktopExtraFilters: boolean,
    // User is required to fill in these inputs in order before other fields become available.
    requiredInOrder: InputName[],
    // If we show customer type selection before any other inputs are shown (simple wizard).
    showOnlyCustomerTypeSelectionFirst?: boolean,
    // Changes the input order in the UI.
    orderOfInputs: InputName[],
    // Required fields that user needs to enter before calendar is shown.
    showCalendarAfterInputs: InputName[],
    // Show the booking info in the calendar view.
    showBookingInfo1: boolean,
    showBookingInfo2: boolean,
    showRemoteBookingInfo: boolean
}

export enum QueryKeys {
    base = "base",
    filters = "filters",
    invoicing_type = "invoicing_type",
    use_geolocation = "use_geolocation",
    date = "date",
    time_of_day = "time_of_day"
}

export interface QueryParams {
    base?: string[],
    filters?: string[],
    invoicing_type?: string,
    date?: string,
    use_geolocation?: string,
    time_of_day?: string
}
